import IAppStore from "app/IAppStore";
import { useSelector } from "react-redux";
import * as UserPreferenceActions from "./userPreferenceCollection.actions";
import { useDispatch } from "react-redux";
import { useEffect, useState } from "react";

export interface UserPreferenceHook<T> {
    value: T;
    update(value: T): void;
    isLoaded: boolean;
}


export function useTypedUserPreference<T>(key: string, 
    importFunc: ((val: string)=> T),
    exportFunc: ((val: T) => string), 
    defaultValue?: T): UserPreferenceHook<T> {

    const [state, setState] = useState({isLoaded: false, value: defaultValue})
    const dispatch = useDispatch();
    const reduxValue = useSelector((m: IAppStore) => m.userPreferenceCollection[key]);

    useEffect(() => {        
        setState(s => ({
            isLoaded: true,
            value: reduxValue == null ? s.value : importFunc(reduxValue)
        }));
    }, [reduxValue])

    function update(value: T) {
        UserPreferenceActions.update(key, exportFunc(value), dispatch);
    }

    return {
        value: state.value,
        update,
        isLoaded: state.isLoaded
    };
}

export function useBooleanUserPreference(key: string, defaultValue?: boolean): UserPreferenceHook<boolean> {
    return useTypedUserPreference(key, m => m === "true", m => m ? "true" : "false", defaultValue);
}

export function useNumberUserPreference(key: string, defaultValue?: number): UserPreferenceHook<number> {
    return useTypedUserPreference(key, Number.parseInt, m => m.toString(), defaultValue);
}

export function useStringArrayUserPreference(key: string, defaultValue?: string[]): UserPreferenceHook<string[]> {
    return useTypedUserPreference(key, (m: string) => m.split(','), (m: string[]) => m.join(','), defaultValue);
}

export function useJsonUserPreference<T>(key: string, defaultValue?: T): UserPreferenceHook<T> {
    return useTypedUserPreference<T>(key, JSON.parse, JSON.stringify, defaultValue);
}

export function useUserPreference(key: string, defaultValue?: string): UserPreferenceHook<string> {
    return useTypedUserPreference(key, m => m, m => m, defaultValue);
}